import { Controller } from '@hotwired/stimulus';

// WebAuthn認証用のStimulusコントローラー
export default class extends Controller {
  static targets = ['registerButton', 'loginButton', 'backButton', 'userIdentifier', 'nickname', 'status'];

  connect() {
    console.log('WebAuthn controller connected, path:', window.location.pathname);

    // 認証処理中フラグを初期化
    this.isAuthenticating = false;

    // I18nメッセージを初期化
    this.initializeI18n();

    // URLを初期化
    this.initializeUrls();

    // WebAuthn APIが利用可能かチェック
    if (!this.isWebAuthnSupported()) {
      this.showError(this.i18n('browserNotSupported'));
      this.disableButtons();
      return;
    }

    // ログイン画面の場合は自動的に認証を開始
    if (this.hasLoginButtonTarget && window.location.pathname.includes('/webauthn_sessions/new')) {
      console.log('WebAuthn login page detected, scheduling auto login');
      // 少し遅延してから自動開始（画面表示を確認するため）
      setTimeout(() => {
        console.log('Auto login timeout fired');
        this.autoLogin();
      }, 500);
    }
  }

  // パスキー登録
  async register(event) {
    event.preventDefault();

    // iOS: ソフトキーボードが開いている場合は先に閉じる
    // キーボード表示中にWebAuthn APIを呼ぶとdocument focusが失われるため
    if (document.activeElement && document.activeElement !== document.body) {
      document.activeElement.blur();
    }

    try {
      this.setStatus(this.i18n('registering'));
      this.disableButton(this.registerButtonTarget);

      // 登録オプションを取得してWebAuthn認証情報を作成
      const options = await this.fetchRegistrationOptions();
      const credential = await this.createCredential(options);

      // 認証情報をサーバーに送信
      await this.submitRegistration(credential);

      this.showSuccess(this.i18n('registered'));
      // パスキー一覧画面へリダイレクト
      setTimeout(() => window.location.href = this.urls.redirect, 1500);
    } catch (error) {
      console.error('WebAuthn registration error:', error);
      this.showError(error.message || this.i18n('registrationFailed'));
    } finally {
      this.enableButton(this.registerButtonTarget);
    }
  }

  // 登録オプションをサーバーから取得
  async fetchRegistrationOptions() {
    const response = await this.makeAuthenticatedRequest(this.urls.registrationOptions, {
      method: 'POST'
    });

    if (!response.ok) {
      throw new Error(this.i18n('registrationOptionsFailed'));
    }

    const options = await response.json();
    return this.prepareRegistrationOptions(options);
  }

  // 登録オプションを準備（Base64デコード）
  prepareRegistrationOptions(options) {
    options.user.id = this.base64ToArrayBuffer(options.user.id);
    options.challenge = this.base64ToArrayBuffer(options.challenge);

    if (options.excludeCredentials) {
      options.excludeCredentials = options.excludeCredentials.map((cred) => ({
        ...cred,
        id: this.base64ToArrayBuffer(cred.id)
      }));
    }

    return options;
  }

  // WebAuthn認証情報を作成
  async createCredential(options) {
    return await navigator.credentials.create({ publicKey: options });
  }

  // 登録情報をサーバーに送信
  async submitRegistration(credential) {
    const response = await this.makeAuthenticatedRequest(this.urls.registrationCreate, {
      method: 'POST',
      body: JSON.stringify({
        id: credential.id,
        rawId: this.arrayBufferToBase64(credential.rawId),
        type: credential.type,
        response: {
          attestationObject: this.arrayBufferToBase64(credential.response.attestationObject),
          clientDataJSON: this.arrayBufferToBase64(credential.response.clientDataJSON)
        },
        nickname: this.hasNicknameTarget ? this.nicknameTarget.value : null
      })
    });

    const result = await response.json();

    if (result.status !== 'ok') {
      throw new Error(result.message || this.i18n('registrationFailed'));
    }

    // サーバーからリダイレクトURLが返された場合は、そのURLを使用
    if (result.redirect_url) {
      this.urls.redirect = result.redirect_url;
    }
  }

  // 自動的にパスキーログインを開始
  async autoLogin() {
    console.log('autoLogin called, isAuthenticating:', this.isAuthenticating);

    // 認証処理中または既にボタンが無効化されている場合はスキップ
    if (this.isAuthenticating || (this.hasLoginButtonTarget && this.loginButtonTarget.disabled)) {
      console.log('autoLogin skipped - already authenticating or button disabled');
      return;
    }

    this.setStatus(this.i18n('loadingPasskey'));

    // 既存のloginメソッドを呼び出し（eventオブジェクトは不要）
    try {
      console.log('autoLogin: calling performLogin()');
      await this.performLogin();
      console.log('autoLogin: performLogin() completed successfully');
    } catch (error) {
      // エラーが発生した場合は手動操作を促す
      console.error('Auto login failed:', error);
      this.setStatus(this.i18n('passkeyNotFound'));
    }
  }

  // パスキーログイン（手動・自動共通）
  async login(event) {
    if (event) {
      event.preventDefault();
    }
    await this.performLogin();
  }

  // パスキーログインの実際の処理
  async performLogin() {
    console.log('performLogin: starting, isAuthenticating:', this.isAuthenticating);

    // 既に認証処理中の場合は何もしない
    if (this.isAuthenticating) {
      console.log('performLogin: already authenticating, returning');
      return;
    }

    this.isAuthenticating = true;
    console.log('performLogin: set isAuthenticating to true');

    let loginSuccessful = false; // ログイン成功フラグを追加

    try {
      this.setStatus(this.i18n('authenticating'));
      if (this.hasLoginButtonTarget) {
        this.disableButton(this.loginButtonTarget);
      }
      if (this.hasBackButtonTarget) {
        this.disableButton(this.backButtonTarget);
      }

      // 認証オプションを取得してWebAuthn認証情報を取得
      const options = await this.fetchAuthenticationOptions();
      const credential = await this.getCredential(options);

      // 認証情報をサーバーに送信してログイン
      await this.submitAuthentication(credential);
      loginSuccessful = true; // ログイン成功時にフラグを設定
    } catch (error) {
      console.error('WebAuthn login error:', error);
      this.showError(error.message || this.i18n('authenticationFailed'));
      throw error; // autoLoginでキャッチするために再スロー
    } finally {
      this.isAuthenticating = false;
      // ログイン成功時はボタンを有効化しない
      if (!loginSuccessful) {
        if (this.hasLoginButtonTarget) {
          this.enableButton(this.loginButtonTarget);
        }
        if (this.hasBackButtonTarget) {
          this.enableButton(this.backButtonTarget);
        }
      }
    }
  }

  // 認証オプションをサーバーから取得
  async fetchAuthenticationOptions() {
    const userIdentifier = this.hasUserIdentifierTarget ? this.userIdentifierTarget.value : null;

    const response = await this.makeAuthenticatedRequest(this.urls.authenticationOptions, {
      method: 'POST',
      body: JSON.stringify({
        user_identifier: userIdentifier
      })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.message || this.i18n('authenticationOptionsFailed'));
    }

    const options = await response.json();
    return this.prepareAuthenticationOptions(options);
  }

  // 認証オプションを準備（Base64デコード）
  prepareAuthenticationOptions(options) {
    options.challenge = this.base64ToArrayBuffer(options.challenge);

    if (options.allowCredentials) {
      options.allowCredentials = options.allowCredentials.map((cred) => ({
        ...cred,
        id: this.base64ToArrayBuffer(cred.id)
      }));
    }

    return options;
  }

  // WebAuthn認証情報を取得
  async getCredential(options) {
    console.log('performLogin: calling navigator.credentials.get()');
    const credential = await navigator.credentials.get({ publicKey: options });
    console.log('performLogin: got credential from WebAuthn API');
    return credential;
  }

  // 認証情報をサーバーに送信
  async submitAuthentication(credential) {
    console.log('performLogin: sending credential to server');
    const response = await this.makeAuthenticatedRequest(this.urls.authenticationCreate, {
      method: 'POST',
      body: JSON.stringify({
        id: credential.id,
        rawId: this.arrayBufferToBase64(credential.rawId),
        type: credential.type,
        response: {
          authenticatorData: this.arrayBufferToBase64(credential.response.authenticatorData),
          clientDataJSON: this.arrayBufferToBase64(credential.response.clientDataJSON),
          signature: this.arrayBufferToBase64(credential.response.signature),
          userHandle: credential.response.userHandle ? this.arrayBufferToBase64(credential.response.userHandle) : null
        }
      })
    });

    console.log('performLogin: parsing server response');
    const result = await response.json();
    console.log('performLogin: server response:', result);

    if (result.status === 'ok') {
      console.log('WebAuthn login successful, redirecting to:', result.redirect_url || '/');
      this.showSuccess(this.i18n('loggedIn'));
      // 即座にリダイレクト
      console.log('performLogin: about to redirect');
      window.location.href = result.redirect_url || '/';
      console.log('performLogin: redirect called');
    } else {
      throw new Error(result.message || this.i18n('authenticationFailed'));
    }
  }

  // ヘルパーメソッド
  isWebAuthnSupported() {
    return window.PublicKeyCredential !== undefined;
  }

  getCSRFToken() {
    return document.querySelector('meta[name="csrf-token"]').getAttribute('content');
  }

  base64ToArrayBuffer(base64) {
    const binaryString = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes.buffer;
  }

  arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.byteLength; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
  }

  setStatus(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message;
      this.statusTarget.className = 'text-info';
    }
  }

  showSuccess(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message;
      this.statusTarget.className = 'text-success';
    }
  }

  showError(message) {
    if (this.hasStatusTarget) {
      this.statusTarget.textContent = message;
      this.statusTarget.className = 'text-danger';
    }
  }

  disableButtons() {
    if (this.hasRegisterButtonTarget) this.disableButton(this.registerButtonTarget);
    if (this.hasLoginButtonTarget) this.disableButton(this.loginButtonTarget);
    if (this.hasBackButtonTarget) this.disableButton(this.backButtonTarget);
  }

  disableButton(button) {
    button.disabled = true;
    button.classList.add('disabled');
  }

  enableButton(button) {
    button.disabled = false;
    button.classList.remove('disabled');
  }

  // 認証付きリクエストを送信する共通メソッド
  async makeAuthenticatedRequest(url, options = {}) {
    const defaultHeaders = {
      'Content-Type': 'application/json',
      'X-CSRF-Token': this.getCSRFToken()
    };

    return await fetch(url, {
      ...options,
      headers: {
        ...defaultHeaders,
        ...(options.headers || {})
      }
    });
  }

  // I18nメッセージを初期化
  initializeI18n() {
    this.i18nMessages = {
      browserNotSupported: this.data.get('i18nBrowserNotSupported'),
      registering: this.data.get('i18nRegistering'),
      registered: this.data.get('i18nRegistered'),
      registrationFailed: this.data.get('i18nRegistrationFailed'),
      registrationOptionsFailed: this.data.get('i18nRegistrationOptionsFailed'),
      loadingPasskey: this.data.get('i18nLoadingPasskey'),
      authenticating: this.data.get('i18nAuthenticating'),
      loggedIn: this.data.get('i18nLoggedIn'),
      authenticationFailed: this.data.get('i18nAuthenticationFailed'),
      authenticationOptionsFailed: this.data.get('i18nAuthenticationOptionsFailed'),
      passkeyNotFound: this.data.get('i18nPasskeyNotFound')
    };
  }

  // I18nメッセージを取得
  i18n(key) {
    return this.i18nMessages[key] || key;
  }

  // URLを初期化
  initializeUrls() {
    // 登録用URL
    this.urls = {
      registrationOptions: this.data.get('urlOptions') || '/users/webauthn_credentials/options',
      registrationCreate: this.data.get('urlCreate') || '/users/webauthn_credentials',
      authenticationOptions: this.data.get('urlOptions') || '/users/webauthn_sessions/options',
      authenticationCreate: this.data.get('urlCreate') || '/users/webauthn_sessions',
      redirect: this.data.get('urlRedirect') || '/users/webauthn_credentials'
    };
  }
}
